/**
 * PDFFormFiller Component
 *
 * User-facing interface for filling out PDF forms.
 * Renders the PDF with overlay input fields and signature capture.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import SignatureCapture from './SignatureCapture';
import { authFetch, fetchCsrfToken } from '../authFetch';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function PDFFormFiller({ template, customer, filledForm, onComplete, onCancel }) {
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [formData, setFormData] = useState({});
  const [signatures, setSignatures] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [pdfError, setPdfError] = useState(null);
  const [signatureModalField, setSignatureModalField] = useState(null);
  const [agentProfile, setAgentProfile] = useState(null);

  const pageContainerRef = useRef(null);

  // Fetch agent profile on mount (for auto-populating agent signature)
  useEffect(() => {
    const fetchAgentProfile = async () => {
      try {
        const response = await authFetch(`${BASE_URL}/api/profile/`);
        if (response.ok) {
          const profile = await response.json();
          setAgentProfile(profile);
        }
      } catch (err) {
        console.error('Failed to fetch agent profile:', err);
      }
    };
    fetchAgentProfile();
  }, []);

  // Get field definitions
  const fieldDefinitions = template.field_definitions || { pages: [] };

  // Initialize form data from existing filled form or prefill from customer/agent
  useEffect(() => {
    if (filledForm) {
      setFormData(filledForm.form_data || {});
      setSignatures(filledForm.signatures || {});
    } else if (customer) {
      // Prefill from customer and agent data based on field mappings
      const prefilled = {};
      (fieldDefinitions.pages || []).forEach((page) => {
        (page.fields || []).forEach((field) => {
          if (field.dataMapping && field.type !== 'signature') {
            const value = getFieldValue(customer, agentProfile, field.dataMapping);
            if (value) {
              prefilled[field.id] = value;
            }
          }
        });
      });
      setFormData(prefilled);
    }
  }, [filledForm, customer, agentProfile, fieldDefinitions]);

  // Get customer field value from mapping
  const getCustomerFieldValue = (cust, mapping) => {
    if (!cust || !mapping) return '';

    // Handle concatenation expressions
    if (mapping.includes('+')) {
      const parts = mapping.split('+');
      return parts
        .map((part) => {
          part = part.trim();
          if ((part.startsWith("'") && part.endsWith("'")) || (part.startsWith('"') && part.endsWith('"'))) {
            return part.slice(1, -1);
          }
          return cust[part] || '';
        })
        .join('');
    }

    return cust[mapping.trim()] || '';
  };

  // Resolve agent.* field mappings from agent profile
  const getAgentFieldValue = (profile, mapping) => {
    if (!profile || !mapping) return '';
    const field = mapping.replace('agent.', '');
    // Profile API returns first_name, last_name, email from user, plus full_name property
    return profile[field] || '';
  };

  // Get field value from either customer or agent based on mapping prefix
  const getFieldValue = (cust, profile, mapping) => {
    if (!mapping) return '';
    if (mapping.startsWith('agent.')) {
      return getAgentFieldValue(profile, mapping);
    }
    return getCustomerFieldValue(cust, mapping);
  };

  // Get fields for current page
  const getCurrentPageFields = useCallback(() => {
    const page = fieldDefinitions.pages?.find((p) => p.pageNumber === currentPage);
    return page?.fields || [];
  }, [fieldDefinitions, currentPage]);

  // Handle PDF load
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPdfError(null);
  };

  const onDocumentLoadError = (error) => {
    console.error('PDF load error:', error);
    setPdfError('Failed to load PDF.');
  };

  // Handle field value change
  const handleFieldChange = (fieldId, value) => {
    setFormData((prev) => ({ ...prev, [fieldId]: value }));
  };

  // Handle signature capture
  const handleSignatureCapture = (sigData) => {
    if (signatureModalField) {
      setSignatures((prev) => ({
        ...prev,
        [signatureModalField.id]: {
          imageBase64: sigData.imageBase64,
          timestamp: sigData.timestamp,
          role: signatureModalField.signatureRole || 'client',
        },
      }));
      setSignatureModalField(null);
    }
  };

  // Clear signature
  const handleClearSignature = (fieldId) => {
    setSignatures((prev) => {
      const newSigs = { ...prev };
      delete newSigs[fieldId];
      return newSigs;
    });
  };

  // Check if all required fields are filled
  const validateForm = () => {
    const allFields = (fieldDefinitions.pages || []).flatMap((p) => p.fields || []);
    const requiredFields = allFields.filter((f) => f.required);

    for (const field of requiredFields) {
      if (field.type === 'signature') {
        if (!signatures[field.id]?.imageBase64) {
          return { valid: false, message: `Required signature missing: ${field.label || field.name}` };
        }
      } else {
        if (!formData[field.id] || formData[field.id].toString().trim() === '') {
          return { valid: false, message: `Required field missing: ${field.label || field.name}` };
        }
      }
    }

    return { valid: true };
  };

  // Check if form is complete (all required signatures captured)
  const isFormComplete = () => {
    const allFields = (fieldDefinitions.pages || []).flatMap((p) => p.fields || []);
    const requiredSigFields = allFields.filter((f) => f.type === 'signature' && f.required);
    return requiredSigFields.every((f) => signatures[f.id]?.imageBase64);
  };

  // Save draft
  const handleSaveDraft = async () => {
    setSaving(true);
    setError('');

    try {
      await fetchCsrfToken();

      const payload = {
        template: template.id,
        customer: customer.id,
        form_data: formData,
        signatures: signatures,
        status: 'draft',
      };

      let response;
      if (filledForm?.id) {
        // Update existing
        response = await authFetch(`${BASE_URL}/api/filled-forms/${filledForm.id}/`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        // Create new
        response = await authFetch(`${BASE_URL}/api/filled-forms/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to save');
      }

      const savedForm = await response.json();
      onComplete?.(savedForm, 'draft');
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // Submit and generate PDF
  const handleSubmit = async () => {
    const validation = validateForm();
    if (!validation.valid) {
      setError(validation.message);
      return;
    }

    setSaving(true);
    setError('');

    try {
      await fetchCsrfToken();

      // Save form data first
      const payload = {
        template: template.id,
        customer: customer.id,
        form_data: formData,
        signatures: signatures,
        status: 'completed',
      };

      let filledFormId = filledForm?.id;

      if (filledFormId) {
        // Update existing
        const updateResponse = await authFetch(`${BASE_URL}/api/filled-forms/${filledFormId}/`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        if (!updateResponse.ok) {
          throw new Error('Failed to save form data');
        }
      } else {
        // Create new
        const createResponse = await authFetch(`${BASE_URL}/api/filled-forms/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        if (!createResponse.ok) {
          throw new Error('Failed to create form');
        }

        const created = await createResponse.json();
        filledFormId = created.id;
      }

      // Generate PDF
      const pdfResponse = await authFetch(`${BASE_URL}/api/filled-forms/${filledFormId}/generate_pdf/`, {
        method: 'POST',
      });

      if (!pdfResponse.ok) {
        throw new Error('Failed to generate PDF');
      }

      const result = await pdfResponse.json();
      onComplete?.(result, 'completed');
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // Apply agent's saved signature
  const applyAgentSignature = (fieldId) => {
    if (agentProfile?.signature_image) {
      setSignatures((prev) => ({
        ...prev,
        [fieldId]: {
          imageBase64: agentProfile.signature_image,
          timestamp: new Date().toISOString(),
          role: 'agent',
        },
      }));
    }
  };

  // Render field input
  const renderFieldInput = (field) => {
    if (field.type === 'signature') {
      const sig = signatures[field.id];
      const isAgentSig = field.signatureRole === 'agent';
      const hasAgentSignature = agentProfile?.has_signature && agentProfile?.signature_image;

      if (sig?.imageBase64) {
        return (
          <div className="w-full h-full bg-white flex flex-col">
            <img src={sig.imageBase64} alt="Signature" className="flex-1 object-contain" />
            <button
              onClick={() => handleClearSignature(field.id)}
              className="text-xs text-red-600 hover:underline"
            >
              Clear
            </button>
          </div>
        );
      }

      // For agent signature fields with saved signature, show quick apply option
      if (isAgentSig && hasAgentSignature) {
        return (
          <div className="w-full h-full bg-blue-50 border-2 border-dashed border-blue-400 text-blue-700 text-xs flex flex-col items-center justify-center hover:bg-blue-100">
            <button
              onClick={() => applyAgentSignature(field.id)}
              className="px-2 py-1 bg-blue-600 text-white rounded text-xs mb-1 hover:bg-blue-700"
            >
              Use My Signature
            </button>
            <button
              onClick={() => setSignatureModalField(field)}
              className="text-xs underline hover:text-blue-800"
            >
              or sign new
            </button>
          </div>
        );
      }

      return (
        <button
          onClick={() => setSignatureModalField(field)}
          className="w-full h-full bg-yellow-50 border-2 border-dashed border-yellow-400 text-yellow-700 text-xs flex items-center justify-center hover:bg-yellow-100"
        >
          Click to Sign
        </button>
      );
    }

    if (field.type === 'checkbox') {
      return (
        <input
          type="checkbox"
          checked={!!formData[field.id]}
          onChange={(e) => handleFieldChange(field.id, e.target.checked)}
          className="w-full h-full cursor-pointer"
        />
      );
    }

    if (field.type === 'date') {
      return (
        <input
          type="date"
          value={formData[field.id] || ''}
          onChange={(e) => handleFieldChange(field.id, e.target.value)}
          className="w-full h-full border-0 bg-transparent text-xs px-1"
          style={{ fontSize: field.fontSize || 10 }}
        />
      );
    }

    // Text field
    return (
      <input
        type="text"
        value={formData[field.id] || ''}
        onChange={(e) => handleFieldChange(field.id, e.target.value)}
        className="w-full h-full border-0 bg-transparent px-1"
        style={{ fontSize: field.fontSize || 10 }}
        placeholder={field.label}
      />
    );
  };

  // Progress indicator
  const allFields = (fieldDefinitions.pages || []).flatMap((p) => p.fields || []);
  const requiredCount = allFields.filter((f) => f.required).length;
  const filledRequired = allFields.filter((f) => {
    if (!f.required) return false;
    if (f.type === 'signature') return !!signatures[f.id]?.imageBase64;
    return formData[f.id] && formData[f.id].toString().trim() !== '';
  }).length;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="bg-white border-b p-3 flex items-center justify-between">
        <div>
          <h2 className="font-semibold">{template.name}</h2>
          <p className="text-sm text-gray-600">
            Filling for: {customer?.first_name} {customer?.last_name}
          </p>
        </div>
        <div className="text-sm text-gray-500">
          Progress: {filledRequired}/{requiredCount} required fields
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border-b border-red-200 p-3 text-red-700 text-sm">
          {error}
          <button onClick={() => setError('')} className="float-right">×</button>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* PDF Viewer */}
        <div className="flex-1 flex flex-col bg-gray-200">
          {/* Toolbar */}
          <div className="bg-white border-b p-2 flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Page Navigation */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                  className="px-2 py-1 bg-gray-200 rounded disabled:opacity-50"
                >
                  &lt;
                </button>
                <span className="text-sm">Page {currentPage} of {numPages || '?'}</span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(numPages || 1, p + 1))}
                  disabled={currentPage >= (numPages || 1)}
                  className="px-2 py-1 bg-gray-200 rounded disabled:opacity-50"
                >
                  &gt;
                </button>
              </div>

              {/* Zoom */}
              <div className="flex items-center gap-2">
                <button onClick={() => setScale((s) => Math.max(0.5, s - 0.1))} className="px-2 py-1 bg-gray-200 rounded">-</button>
                <span className="text-sm">{Math.round(scale * 100)}%</span>
                <button onClick={() => setScale((s) => Math.min(2, s + 0.1))} className="px-2 py-1 bg-gray-200 rounded">+</button>
              </div>
            </div>
          </div>

          {/* PDF Display */}
          <div className="flex-1 overflow-auto p-4">
            {pdfError ? (
              <div className="text-center text-red-600 p-8">{pdfError}</div>
            ) : (
              <Document
                file={template.pdf_url}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={<div className="text-center p-8">Loading PDF...</div>}
              >
                <div
                  ref={pageContainerRef}
                  className="relative inline-block shadow-lg"
                  style={{ transform: `scale(${scale})`, transformOrigin: 'top left' }}
                >
                  <Page pageNumber={currentPage} renderTextLayer={false} renderAnnotationLayer={false} />

                  {/* Field Overlays */}
                  {getCurrentPageFields().map((field) => (
                    <div
                      key={field.id}
                      className={`absolute ${
                        field.required && !formData[field.id] && field.type !== 'signature'
                          ? 'ring-2 ring-red-400'
                          : field.required && field.type === 'signature' && !signatures[field.id]
                          ? 'ring-2 ring-red-400'
                          : ''
                      }`}
                      style={{
                        left: field.x,
                        top: field.y,
                        width: field.width,
                        height: field.height,
                      }}
                    >
                      {renderFieldInput(field)}
                    </div>
                  ))}
                </div>
              </Document>
            )}
          </div>
        </div>

        {/* Side panel - field list */}
        <div className="w-64 bg-white border-l overflow-y-auto">
          <div className="p-4">
            <h3 className="font-semibold mb-3">Form Fields</h3>

            {(fieldDefinitions.pages || []).map((page) => (
              <div key={page.pageNumber} className="mb-4">
                <button
                  onClick={() => setCurrentPage(page.pageNumber)}
                  className={`text-sm font-medium mb-2 ${currentPage === page.pageNumber ? 'text-blue-600' : 'text-gray-600'}`}
                >
                  Page {page.pageNumber}
                </button>
                <div className="space-y-1 pl-2">
                  {(page.fields || []).map((field) => {
                    const isFilled = field.type === 'signature' ? !!signatures[field.id] : !!formData[field.id];
                    return (
                      <div
                        key={field.id}
                        onClick={() => setCurrentPage(page.pageNumber)}
                        className={`text-xs p-1 rounded cursor-pointer ${
                          isFilled ? 'bg-green-50 text-green-700' : field.required ? 'bg-red-50 text-red-700' : 'bg-gray-50'
                        }`}
                      >
                        {field.label || field.name}
                        {field.required && !isFilled && <span className="text-red-500 ml-1">*</span>}
                        {isFilled && <span className="text-green-500 ml-1">✓</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-white border-t p-3 flex justify-between items-center">
        <button
          onClick={onCancel}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
        >
          Cancel
        </button>

        <div className="flex gap-3">
          <button
            onClick={handleSaveDraft}
            disabled={saving}
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 disabled:bg-gray-400"
          >
            {saving ? 'Saving...' : 'Save Draft'}
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || !isFormComplete()}
            className={`px-4 py-2 rounded text-white ${
              saving || !isFormComplete() ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
            }`}
            title={!isFormComplete() ? 'Complete all required signatures first' : 'Submit and generate PDF'}
          >
            {saving ? 'Processing...' : 'Submit & Generate PDF'}
          </button>
        </div>
      </div>

      {/* Signature Modal */}
      {signatureModalField && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">
              {signatureModalField.label || 'Capture Signature'}
            </h3>
            <SignatureCapture
              label={signatureModalField.label || 'Sign here'}
              signatureRole={signatureModalField.signatureRole || 'client'}
              width={400}
              height={150}
              onCapture={handleSignatureCapture}
            />
            <div className="mt-4 text-right">
              <button
                onClick={() => setSignatureModalField(null)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
